import {
  Box,
  Flex,
  Text
} from '@huspy/briks-web';
import { forwardRef } from 'react';
import { Divider } from '@huspy/styled-system/jsx';
import useGetVaultConfig from '@modules/vault/hooks/queries/useVaultConfig';
import { APPLICANT_TYPE } from '@modules/opportunities/entities/opportunity/const';
import { useTranslation } from 'react-i18next';
import Modal from '@components/Modal';
import { VaultActions, VaultDisclaimer } from '@modules/opportunities/presentation/pages/CaseCreation/components/VaultConfiguration/components/VaultOverview';
import { imagesPath } from '@shared/css.const';
import { validators } from '@huspy/forge/shared';
import BanksSelection from './BanksSelection';
import DocumentsChecklist from './DocumentsChecklist';

export type AddClientModalRef = { open(): void; close(): void } | undefined;

type Props = {
  oppId: string;
  onClose?(): void;
  isOpen?: boolean;
};

// eslint-disable-next-line prefer-arrow-callback
const VaultConfigurator = forwardRef<AddClientModalRef, Props>(function _VaultConfigurator({ oppId, isOpen }, ref) {
  const { data, isLoading: isVaultConfigLoading } = useGetVaultConfig(oppId);
  const { t } = useTranslation();
  const mainApplicant = data?.applicants.find(
    (app) => app.applicant_type === APPLICANT_TYPE.mainApplicant
  );

  const handleOnClose = () => {
    // @ts-ignore
    ref?.current?.close();
  };

  const fullName = `${mainApplicant?.first_name} ${mainApplicant?.last_name}`;
  const isBankSelected = data?.selected_banks?.length !== 0;
  const email = mainApplicant?.email;
  const mobile = mainApplicant?.mobile;
  const missingPhoneOrEmail = !mobile || !email;
  const isValidData = validators.phoneNumberValidator.safeParse(mobile).success
      && validators.emailFieldValidator.safeParse(email).success;
  const isDisabled = missingPhoneOrEmail || !isValidData;

  return (
    <Modal
      title={ (
        <>
          <Text size='3xl' fontWeight='semibold'>
            {t('vault.config.title')}
          </Text>
          <Text size='lg' color='text-secondary'>
            {t('vault.config.subtitle-1')}
            <br />
            {t('vault.config.subtitle-2')}
          </Text>
        </>
      ) }
      ref={ ref }
      opened={ isOpen }
      onClose={ handleOnClose }
      size={ 644 }
      styles={ {
        header: {
          backgroundImage: `url(${imagesPath}/vault-bg.svg)`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '100% 100%',
        },
      } }
    >
      <Flex direction='column' gap='2'>
        {!isVaultConfigLoading && (
          <VaultDisclaimer
            fullName={ fullName }
            hasApplicant={ !!mainApplicant }
            isBankSelected={ isBankSelected }
            email={ email }
            mobile={ mobile }
            missingPhoneOrEmail={ missingPhoneOrEmail }
            isValidData={ isValidData }
          />
        )}
        <Box>
          <Text size='2xl' fontWeight='semibold'>
            {t('vault.config.generalChecklist')}
          </Text>
          <Text size='md' color='text-secondary'>
            {t('vault.config.generalChecklistSubtitle')}
          </Text>
        </Box>
        <BanksSelection
          oppId={ oppId }
          vaultData={ data }
          isVaultConfigLoading={ isVaultConfigLoading }
          isDisabled={ isDisabled }
        />
        <Divider color='neutral.3' my='4' />
        <DocumentsChecklist
          vaultData={ data }
          oppId={ oppId }
        />
        <Divider color='neutral.3' my='4' />
        <VaultActions
          opportunityId={ oppId }
          fullName={ fullName }
          email={ email }
          isDisabled={ isDisabled || !isBankSelected || isVaultConfigLoading }
          clientId={ undefined }
        />
      </Flex>
    </Modal>
  );
});

export default VaultConfigurator;
